import { useTu } from "@src/appV2/i18n/useTu";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Box } from "@mui/material";
import { Text } from "../components/Text";
export type ChatMessageReadStatus = "sent" | "read";

/**
 * Derive the read-status indicator for a bubble. Incoming messages never
 * show a status; outgoing messages show "read" when the counter-party has
 * read them, otherwise "sent".
 */
export function getOutgoingReadStatus(
  isIncoming: boolean,
  readByOther: boolean | undefined
): ChatMessageReadStatus | undefined {
  if (isIncoming) {
    return undefined;
  }
  return readByOther ? "read" : "sent";
}

export interface ChatMessageTimestampProps {
  senderName: string;
  timestamp: string;
  /**
   * When set, renders a small WhatsApp-style check indicator after the
   * timestamp. `"sent"` = single check (delivered to Sendbird); `"read"` =
   * double check (counter-party has read it). Omit for incoming messages.
   */
  readStatus?: ChatMessageReadStatus;
  /** When true, renders a small "· edited" marker after the timestamp. */
  isEdited?: boolean;
}

export function ChatMessageTimestamp(props: ChatMessageTimestampProps) {
  const { tu } = useTu("chat");

  const { senderName, timestamp, readStatus, isEdited = false } = props;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Text
        inline
        variant="caption"
        sx={(theme) => ({
          color: theme.palette.base?.inactive, // 35% opacity per Figma
        })}
      >
        {senderName}
      </Text>
      <Text
        inline
        variant="overline"
        sx={(theme) => ({
          color: theme.palette.base?.inactive,
        })}
      >
        {timestamp}
      </Text>
      {isEdited && (
        <Text
          inline
          variant="overline"
          sx={(theme) => ({
            color: theme.palette.base?.inactive,
          })}
        >
          {tu("· edited")}
        </Text>
      )}
      {readStatus === "sent" && (
        <CheckIcon
          aria-label={tu("Sent")}
          role="img"
          sx={(theme) => ({
            fontSize: 14,
            color: theme.palette.base?.inactive,
          })}
        />
      )}
      {readStatus === "read" && (
        <DoneAllIcon
          aria-label={tu("Read")}
          role="img"
          sx={(theme) => ({
            fontSize: 14,
            color: theme.palette.primary.main,
          })}
        />
      )}
    </Box>
  );
}
